//@ts-nocheck
import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { XGModal, XGButton, XGRadio, XGDatePicker } from 'xg-cl';
import { blue } from '@mui/material/colors';
import { Plus, Trash } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  createOrUpdatePaymentPlan,
  getPaymentPlan
} from '../../../services/paymentplan/paymentplan';
import {
  updateGlobalFeeAllocations,
  updateTripFeeAllocations,
  getFeeAllocationForPaymentRef,
  getTripFees
} from '../../../services/fee/fee';
import { getTrip } from '../../../services/trip/trip';
import {
  getTenantSettings,
  setTenantSettings,
  getTripSettings,
  setTripSettings
} from '../../../services/tenant/tenant';
import { ErrorToast } from '../../../utils/alerts';
import { getSettingMarkdownValue } from '../../../utils/markdownHelper';
import Editor from '../../Editor/Editor';
import SegmentedToggle from '../../Common/SegmentedToggle/SegmentedToggle';
// Using XGModal directly so we can control button texts
import PropTypes from 'prop-types';
import './PaymentPlansPanel.css';
import { format, subDays, differenceInCalendarDays } from 'date-fns';

const PAYMENT_INSTRUCTIONS_SETTINGCODE = 'PAYMENTINSTRUCTIONS';
const PAYMENT_INSTRUCTIONS_MAX_LENGTH = 500;

/**
 * Functional React component for managing payment plans.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the payment plans panel component.
 * @param {number|null} [props.tripId] - Trip ID for trip-specific plans, null for global plans.
 *
 * @returns {JSX.Element} React element representing the payment plans panel.
 */
const PaymentPlansPanel = ({
  tripId = null,
  showPaymentSchedule = true,
  showTogglesAndFees = true
}) => {
  const { t } = useTranslation('settings');
  const { tripNavigation, enablePaymentInstructionsForNoPaymentBookings } =
    useFlags();
  const enablePaymentInstructionsFeature = Boolean(
    enablePaymentInstructionsForNoPaymentBookings
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [tripStartDate, setTripStartDate] = useState(null);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [savedPaymentInstructions, setSavedPaymentInstructions] = useState('');
  const [paymentInstructionsLength, setPaymentInstructionsLength] = useState(0);

  // Normalize any date-like input to a Date at 12:00 local time to avoid DST-related off-by-ones
  const toLocalNoon = (value) => {
    if (!value) return null;
    let d = null;
    if (value instanceof Date) {
      d = value;
    } else if (typeof value === 'string') {
      // If we get a YYYY-MM-DD string (commonly returned by APIs), treat as local date,
      // not UTC, and set time to 12:00 to avoid DST transitions at midnight.
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const year = parseInt(m[1], 10);
        const month = parseInt(m[2], 10) - 1;
        const day = parseInt(m[3], 10);
        return new Date(year, month, day, 12, 0, 0, 0);
      }
      // Fallback: let JS parse and then normalize to noon
      const parsed = new Date(value);
      if (!isNaN(parsed)) {
        d = parsed;
      }
    } else if (typeof value === 'number') {
      d = new Date(value);
    }

    if (!d || isNaN(d)) return null;
    return new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate(),
      12, // noon local time
      0,
      0,
      0
    );
  };

  // Helper function to parse deposit amount string
  const parseDepositAmount = (depositAmount) => {
    if (!depositAmount) return { type: 'per_person', amount: '' };

    // Parse nomenclature: "100pp", "200pb", "25%"
    if (depositAmount.endsWith('%')) {
      const amount = depositAmount.replace('%', '').trim();
      return { type: 'percentage', amount };
    } else if (depositAmount.endsWith('pb')) {
      const amount = depositAmount.replace('pb', '').trim();
      return { type: 'per_booking', amount };
    } else if (depositAmount.endsWith('pp')) {
      const amount = depositAmount.replace('pp', '').trim();
      return { type: 'per_person', amount };
    }

    // Default fallback
    return { type: 'per_person', amount: depositAmount };
  };

  // Helper function to parse payment amount string (for second/third/fourth payments)
  const parsePaymentAmount = (paymentAmount) => {
    if (!paymentAmount) return { type: 'per_person', amount: '' };

    const amountStr = String(paymentAmount);
    // Parse nomenclature: "100pp", "200pb", "25%"
    if (amountStr.endsWith('%')) {
      const amount = amountStr.replace('%', '').trim();
      return { type: 'percentage', amount };
    } else if (amountStr.endsWith('pb')) {
      const amount = amountStr.replace('pb', '').trim();
      return { type: 'per_booking', amount };
    } else if (amountStr.endsWith('pp')) {
      const amount = amountStr.replace('pp', '').trim();
      return { type: 'per_person', amount };
    }

    // Legacy: plain numbers default to percentage for backward compatibility
    return { type: 'percentage', amount: amountStr };
  };

  // Single payment plan object representing the complete state
  const [paymentPlan, setPaymentPlan] = useState({
    paymentplanid: null,
    tenantid: null,
    depositAmount: '',
    depositType: 'per_person', // 'per_person', 'per_booking', 'percentage'
    depositAmountValue: '',
    finalPaymentDue: '',
    allowPartialPayment: false,
    allowBookingWithoutPayment: false,
    additionalPayments: [],
    // Fee Allocation settings - initialize as null to prevent saving defaults
    creditCardFeeAllocation: null, // 'organizer' or 'passenger'
    achFeeAllocation: null, // 'organizer' or 'passenger'
    platformFeeAllocation: null // 'organizer' or 'passenger'
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const saveTimeoutRef = useRef(null);
  const initialLoadRef = useRef(true);
  const isSavingPaymentInstructionsRef = useRef(false);
  const lastSavedPaymentInstructionsRef = useRef('');

  // Handle fee allocation changes
  const handleFeeAllocationChange = (field, value) => {
    updatePaymentPlan({
      [field]: value
    });
  };

  const loadFeeAllocations = useCallback(async () => {
    try {
      if (tripId === null) {
        // For global payment plans, fetch the current fee allocation states
        const [creditCardResponse, achResponse, platformResponse] =
          await Promise.all([
            getFeeAllocationForPaymentRef('card'),
            getFeeAllocationForPaymentRef('us_bank_account'),
            getFeeAllocationForPaymentRef('platform')
          ]);

        // Helper function to determine the allocation value
        const toAllocation = (resp) => {
          if (
            !resp ||
            resp.success !== true ||
            typeof resp.isIncluded !== 'boolean'
          )
            return null;
          return resp.isIncluded ? 'organizer' : 'participant';
        };

        return {
          creditCardFeeAllocation: toAllocation(creditCardResponse),
          achFeeAllocation: toAllocation(achResponse),
          platformFeeAllocation: toAllocation(platformResponse)
        };
      }
      // Trip-level fee allocations: derive from trip fees (trip.tripFees)
      const { data: tripFees, success } = await getTripFees(tripId);
      if (!success || !Array.isArray(tripFees) || tripFees.length === 0) {
        // Fallback to global fee allocations when the trip has no fees defined
        const [cc, ach, platform] = await Promise.all([
          getFeeAllocationForPaymentRef('card'),
          getFeeAllocationForPaymentRef('us_bank_account'),
          getFeeAllocationForPaymentRef('platform')
        ]);

        const toAllocation = (resp) => {
          if (
            !resp ||
            resp.success !== true ||
            typeof resp.isIncluded !== 'boolean'
          )
            return null;
          return resp.isIncluded ? 'organizer' : 'participant';
        };
        return {
          creditCardFeeAllocation: toAllocation(cc),
          achFeeAllocation: toAllocation(ach),
          platformFeeAllocation: toAllocation(platform)
        };
      }

      // Helper to map isincluded => allocation value
      const toAllocationFromIncluded = (isIncluded) =>
        isIncluded ? 'organizer' : 'participant';

      // Group by paymentref to align with Trip Pricing behavior
      const byRef = tripFees.reduce((acc, fee) => {
        const raw = fee?.paymentref;
        const key =
          raw === null || raw === undefined || raw === '' ? 'platform' : raw;
        acc[key] = acc[key] || [];
        acc[key].push(fee);
        return acc;
      }, {});

      const ccIncluded = Array.isArray(byRef['card'])
        ? byRef['card'].some((f) => f?.isincluded === true)
        : null;
      const achIncluded = Array.isArray(byRef['us_bank_account'])
        ? byRef['us_bank_account'].some((f) => f?.isincluded === true)
        : null;
      const platformIncluded = Array.isArray(byRef['platform'])
        ? byRef['platform'].some((f) => f?.isincluded === true)
        : null;

      return {
        creditCardFeeAllocation:
          ccIncluded === null ? null : toAllocationFromIncluded(ccIncluded),
        achFeeAllocation:
          achIncluded === null ? null : toAllocationFromIncluded(achIncluded),
        platformFeeAllocation:
          platformIncluded === null
            ? null
            : toAllocationFromIncluded(platformIncluded)
      };
    } catch (error) {
      console.error(
        t('errors:errorLoadingFeeAllocations') ||
          'Error loading fee allocations:',
        error
      );
      return {
        creditCardFeeAllocation: null,
        achFeeAllocation: null,
        platformFeeAllocation: null
      };
    }
  }, [tripId, t]);

  const loadPaymentInstructions = useCallback(async () => {
    if (!enablePaymentInstructionsFeature) {
      setPaymentInstructions('');
      setSavedPaymentInstructions('');
      lastSavedPaymentInstructionsRef.current = '';
      setPaymentInstructionsLength(0);
      return;
    }

    try {
      const response =
        tripId === null
          ? await getTenantSettings(PAYMENT_INSTRUCTIONS_SETTINGCODE)
          : await getTripSettings(tripId, PAYMENT_INSTRUCTIONS_SETTINGCODE);
      const markdown = getSettingMarkdownValue(response?.data?.settingvalue);
      setPaymentInstructions(markdown);
      setSavedPaymentInstructions(markdown);
      lastSavedPaymentInstructionsRef.current = markdown;
      setPaymentInstructionsLength(markdown.length || 0);
    } catch (error) {
      console.error(
        t('settings:paymentPlan.paymentInstructionsLoadError') ||
          'Error loading payment instructions:',
        error
      );
    }
  }, [tripId, enablePaymentInstructionsFeature, t]);

  const savePaymentInstructionsOnBlur = useCallback(async () => {
    if (!enablePaymentInstructionsFeature) return;
    const nextValue = paymentInstructions || '';
    if (nextValue === lastSavedPaymentInstructionsRef.current) return;
    if (isSavingPaymentInstructionsRef.current) return;
    isSavingPaymentInstructionsRef.current = true;

    try {
      const response =
        tripId === null
          ? await setTenantSettings(PAYMENT_INSTRUCTIONS_SETTINGCODE, [
              { markdown: nextValue }
            ])
          : await setTripSettings(tripId, PAYMENT_INSTRUCTIONS_SETTINGCODE, [
              { markdown: nextValue }
            ]);

      if (!response?.data || response?.error) {
        ErrorToast(t('settings:paymentPlan.paymentInstructionsSaveError'));
        return;
      }

      setSavedPaymentInstructions(nextValue);
      lastSavedPaymentInstructionsRef.current = nextValue;
    } catch (error) {
      ErrorToast(t('settings:paymentPlan.paymentInstructionsSaveError'));
    } finally {
      isSavingPaymentInstructionsRef.current = false;
    }
  }, [tripId, enablePaymentInstructionsFeature, paymentInstructions, t]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Always load fee allocations (global or trip-level behavior handled inside)
        const feeAllocations = await loadFeeAllocations();

        // Try to load trip-specific plan first (if tripId provided)
        let planResponse = null;
        try {
          planResponse = await getPaymentPlan(tripId);
        } catch (err) {
          // If trip plan not found and we're in trip mode, fall back to global plan values
          if (tripId !== null) {
            try {
              planResponse = await getPaymentPlan(null);
            } catch (globalErr) {
              // Keep null; will result in defaults below
              planResponse = null;
            }
          } else {
            throw err;
          }
        }

        // If tripId is provided, fetch the trip to get the start date
        if (tripId !== null) {
          try {
            const tripResp = await getTrip(tripId);
            const start =
              tripResp?.data?.startdate ||
              tripResp?.data?.trip?.startdate ||
              tripResp?.startdate ||
              null;
            setTripStartDate(toLocalNoon(start));
          } catch (tripErr) {
            console.warn('Unable to load trip start date:', tripErr);
            setTripStartDate(null);
          }
        } else {
          setTripStartDate(null);
        }

        if (planResponse && planResponse.data) {
          // Check if data is nested under 'paymentplan' property
          const plan = planResponse.data.paymentplan || planResponse.data;

          // Build additional payments array from the plan data
          const additionalPayments = [];

          if (plan.secondpaymentdue && plan.secondpaymentamount) {
            const { type, amount } = parsePaymentAmount(
              plan.secondpaymentamount
            );
            additionalPayments.push({
              id: 'second',
              daysBeforeDeparture: plan.secondpaymentdue,
              amount: plan.secondpaymentamount,
              amountType: type,
              amountValue: amount
            });
          }

          if (plan.thirdpaymentdue && plan.thirdpaymentamount) {
            const { type, amount } = parsePaymentAmount(
              plan.thirdpaymentamount
            );
            additionalPayments.push({
              id: 'third',
              daysBeforeDeparture: plan.thirdpaymentdue,
              amount: plan.thirdpaymentamount,
              amountType: type,
              amountValue: amount
            });
          }

          if (plan.fourthpaymentdue && plan.fourthpaymentamount) {
            const { type, amount } = parsePaymentAmount(
              plan.fourthpaymentamount
            );
            additionalPayments.push({
              id: 'fourth',
              daysBeforeDeparture: plan.fourthpaymentdue,
              amount: plan.fourthpaymentamount,
              amountType: type,
              amountValue: amount
            });
          }

          // Parse deposit amount into type and value
          const { type, amount } = parseDepositAmount(plan.depositamount || '');

          // Determine if the loaded plan is for this trip
          const isTripPlan =
            tripId !== null && Number(plan?.tripid) === Number(tripId);
          // If we fell back to global plan for a trip, ensure paymentplanid is null (so first save creates)
          const resolvedPaymentPlanId =
            tripId === null
              ? plan.paymentplanid || null
              : isTripPlan
                ? plan.paymentplanid || null
                : null;

          setPaymentPlan((prev) => ({
            paymentplanid: resolvedPaymentPlanId,
            tenantid: plan.tenantid || null,
            depositAmount: plan.depositamount || '',
            depositType: type,
            depositAmountValue: amount,
            finalPaymentDue: plan.finalpaymentdue || '',
            allowPartialPayment: plan.allowpartialpayment || false,
            allowBookingWithoutPayment:
              plan.allowbookingwithoutpayment || false,
            additionalPayments,
            // Initialize fee allocations strictly from GET /fee results for global settings
            creditCardFeeAllocation:
              feeAllocations?.creditCardFeeAllocation ??
              prev.creditCardFeeAllocation,
            achFeeAllocation:
              feeAllocations?.achFeeAllocation ?? prev.achFeeAllocation,
            platformFeeAllocation:
              feeAllocations?.platformFeeAllocation ??
              prev.platformFeeAllocation
          }));
        }

        await loadPaymentInstructions();
      } catch (error) {
        console.error(
          t('errors:errorLoadingPaymentPlanData') || 'Error loading data:',
          error
        );
        ErrorToast(t('errors:failedToLoadPaymentPlanData'));
      } finally {
        setLoading(false);
        initialLoadRef.current = false;
      }
    };

    loadData();
  }, [tripId, loadFeeAllocations, loadPaymentInstructions, t]);

  // Helper function to format deposit amount for API
  const formatDepositAmount = (type, amount) => {
    if (!amount || amount.trim() === '') return '';

    let suffix = 'pp'; // default
    if (type === 'per_booking') suffix = 'pb';
    else if (type === 'percentage') suffix = '%';

    return `${amount}${suffix}`;
  };

  // Helper function to format payment amount for API (second/third/fourth)
  const formatPaymentAmount = (type, amount) => {
    if (!amount || amount.toString().trim() === '') return '';

    let suffix = 'pp'; // default
    if (type === 'per_booking') suffix = 'pb';
    else if (type === 'percentage') suffix = '%';

    return `${amount}${suffix}`;
  };

  // Update payment plan object
  const updatePaymentPlan = (updates) => {
    // Mark that the user has interacted with the form to enable auto-save after initial load
    if (!hasInteracted) setHasInteracted(true);
    setPaymentPlan((prev) => {
      const newPlan = { ...prev, ...updates };

      // Auto-update depositAmount when type or value changes
      if (
        updates.depositType !== undefined ||
        updates.depositAmountValue !== undefined
      ) {
        const type =
          updates.depositType !== undefined
            ? updates.depositType
            : prev.depositType;
        const amount =
          updates.depositAmountValue !== undefined
            ? updates.depositAmountValue
            : prev.depositAmountValue;
        newPlan.depositAmount = formatDepositAmount(type, amount);
      }

      return newPlan;
    });
  };

  // Helper function to validate numeric inputs
  const isValidNumber = (value) => {
    if (!value || value.toString().trim() === '') return true; // Empty is valid (optional)
    const str = value.toString().trim();
    return /^\d+(\.\d+)?$/.test(str); // Only allow digits and optional decimal point
  };

  // Helper function to validate additional payment amounts
  const isValidAdditionalPaymentAmount = useCallback(
    (value, amountType = 'percentage') => {
      if (!value || value.toString().trim() === '')
        return {
          valid: false,
          error: t('settings:paymentPlan.validation.amountIsRequired')
        };
      const str = value.toString().trim();

      // Check if it's a valid number format
      if (!/^\d+(\.\d+)?$/.test(str)) {
        return {
          valid: false,
          error: t('settings:paymentPlan.validation.mustBeValidNumber')
        };
      }

      const num = parseFloat(str);
      if (isNaN(num)) {
        return {
          valid: false,
          error: t('settings:paymentPlan.validation.mustBeValidNumber')
        };
      }

      if (num <= 0) {
        return {
          valid: false,
          error: t('settings:paymentPlan.validation.amountMustBeGreaterThan0')
        };
      }

      // Only validate upper limit for percentage payments
      if (amountType === 'percentage' && num > 100) {
        return {
          valid: false,
          error: t('settings:paymentPlan.validation.amountMustBeLessThan100')
        };
      }

      return { valid: true, error: null };
    },
    [t]
  );

  // Helper function to validate additional payment due days
  const isValidAdditionalPaymentDays = useCallback(
    (value, finalPaymentDue, previousPaymentDays = null) => {
      if (!value || value.toString().trim() === '')
        return {
          valid: false,
          error: t('settings:paymentPlan.validation.dueDaysIsRequired')
        };
      const str = value.toString().trim();

      // Check if it's a valid number format
      if (!/^\d+$/.test(str)) {
        return {
          valid: false,
          error: t('settings:paymentPlan.validation.mustBeValidNumber')
        };
      }

      const num = parseInt(str);
      if (isNaN(num)) {
        return {
          valid: false,
          error: t('settings:paymentPlan.validation.mustBeValidNumber')
        };
      }

      const finalDue = parseInt(finalPaymentDue) || 0;
      if (num <= finalDue) {
        return {
          valid: false,
          error: t(
            'settings:paymentPlan.validation.mustBeGreaterThanFinalDue',
            {
              days: finalDue
            }
          )
        };
      }

      if (previousPaymentDays !== null) {
        const prevDays = parseInt(previousPaymentDays) || 0;
        if (num >= prevDays) {
          return {
            valid: false,
            error: t(
              'settings:paymentPlan.validation.mustBeLessThanPreviousPayment',
              { days: prevDays }
            )
          };
        }
      }

      return { valid: true, error: null };
    },
    [t]
  );

  const validateFormData = useCallback(() => {
    const errors = {};

    // Validate deposit amount value
    if (!isValidNumber(paymentPlan.depositAmountValue)) {
      errors.depositAmountValue = t(
        'settings:paymentPlan.validation.depositAmountMustBeValidNumber'
      );
    }

    // Validate deposit percentage is <= 100
    if (
      paymentPlan.depositType === 'percentage' &&
      paymentPlan.depositAmountValue
    ) {
      const depositPercent = parseFloat(paymentPlan.depositAmountValue);
      if (depositPercent > 100) {
        errors.depositAmountValue = t(
          'settings:paymentPlan.validation.depositPercentageMustBe100OrLess'
        );
      }
    }

    // Validate final payment due
    if (!isValidNumber(paymentPlan.finalPaymentDue)) {
      errors.finalPaymentDue = t(
        'settings:paymentPlan.validation.finalPaymentDueMustBeValidNumberOfDays'
      );
    }

    const finalPaymentDue = parseInt(paymentPlan.finalPaymentDue) || 0;

    // Validate additional payments
    paymentPlan.additionalPayments.forEach((payment, index) => {
      const amountFieldKey = `payment_${payment.id}_amount`;
      const daysFieldKey = `payment_${payment.id}_days`;

      // Validate amount - only show error if field has been touched
      if (touchedFields[amountFieldKey]) {
        const amountValidation = isValidAdditionalPaymentAmount(
          payment.amountValue,
          payment.amountType
        );
        if (!amountValidation.valid) {
          errors[amountFieldKey] = amountValidation.error;
        }
      }

      // Validate due days - only show error if field has been touched
      if (touchedFields[daysFieldKey]) {
        const previousPaymentDays =
          index > 0
            ? paymentPlan.additionalPayments[index - 1].daysBeforeDeparture
            : null;
        const daysValidation = isValidAdditionalPaymentDays(
          payment.daysBeforeDeparture,
          finalPaymentDue,
          previousPaymentDays
        );
        if (!daysValidation.valid) {
          errors[daysFieldKey] = daysValidation.error;
        }
      }
    });

    // Validate that sum of percentage-based interim payments doesn't exceed 100%
    const percentagePayments = paymentPlan.additionalPayments.filter(
      (payment) =>
        payment.amountType === 'percentage' &&
        payment.amountValue &&
        payment.amountValue.toString().trim() !== ''
    );

    if (percentagePayments.length > 0) {
      const totalPercentage = percentagePayments.reduce((sum, payment) => {
        const value = parseFloat(payment.amountValue);
        return sum + (isNaN(value) ? 0 : value);
      }, 0);

      if (totalPercentage > 100) {
        // Add error to all percentage-based payment fields that have been touched
        percentagePayments.forEach((payment) => {
          const amountFieldKey = `payment_${payment.id}_amount`;
          if (touchedFields[amountFieldKey]) {
            errors[amountFieldKey] = t(
              'settings:paymentPlan.validation.percentagePaymentsSumExceeds100',
              { total: totalPercentage.toFixed(2) }
            );
          }
        });
      }
    }

    return errors;
  }, [
    t,
    paymentPlan,
    touchedFields,
    isValidAdditionalPaymentAmount,
    isValidAdditionalPaymentDays
  ]);

  const saveFeeAllocationsToAPI = useCallback(async () => {
    try {
      // Only process fee allocations if we're at the global level (tripId is null)
      if (tripId === null) {
        // Save each fee allocation separately using the new payment reference based approach
        const creditCardResponse = await updateGlobalFeeAllocations(
          'card',
          paymentPlan.creditCardFeeAllocation === 'organizer'
        );

        const achResponse = await updateGlobalFeeAllocations(
          'us_bank_account',
          paymentPlan.achFeeAllocation === 'organizer'
        );

        const platformResponse = await updateGlobalFeeAllocations(
          'platform',
          paymentPlan.platformFeeAllocation === 'organizer'
        );

        // Check if any of the updates failed
        if (
          !creditCardResponse.success ||
          !achResponse.success ||
          !platformResponse.success
        ) {
          const error =
            creditCardResponse.error ||
            achResponse.error ||
            platformResponse.error;
          throw new Error(
            error || t('errors:failedToSaveOneOrMoreFeeAllocations')
          );
        }
      } else {
        // Trip-level fee allocation - save to trip.tripFee table (unchanged)
        // If all are null we skip (handled elsewhere / unchanged)
        const {
          creditCardFeeAllocation,
          achFeeAllocation,
          platformFeeAllocation
        } = paymentPlan;
        const allNull =
          creditCardFeeAllocation === null &&
          achFeeAllocation === null &&
          platformFeeAllocation === null;
        if (!allNull) {
          const feeAllocations = {
            creditCardFeeAllocation,
            achFeeAllocation,
            platformFeeAllocation
          };
          const response = await updateTripFeeAllocations(
            tripId,
            feeAllocations
          );
          if (!response.success) {
            throw new Error(
              response.error || t('errors:failedToSaveTripFeeAllocations')
            );
          }
        }
      }
    } catch (error) {
      console.error(
        t('errors:errorSavingFeeAllocations') ||
          'Error saving fee allocations:',
        error
      );
      ErrorToast(
        error.message || t('errors:failedToSaveFeeAllocationSettings')
      );
    }
  }, [tripId, paymentPlan, t]);

  const savePaymentPlanToAPI = useCallback(async () => {
    // Validate before sending
    const validationErrors = validateFormData();
    setFieldErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      console.warn(
        t('settings:paymentPlan.validation.validationErrorsNotSaving') ||
          'Validation errors - not saving:',
        validationErrors
      );
      return;
    }

    try {
      setSaving(true);

      // Derive final payment due (allow null when cleared)
      const finalDue =
        paymentPlan.finalPaymentDue === '' ||
        paymentPlan.finalPaymentDue === null ||
        paymentPlan.finalPaymentDue === undefined
          ? null
          : parseInt(paymentPlan.finalPaymentDue);

      const paymentPlanData = {
        depositamount: paymentPlan.depositAmount,
        finalpaymentdue: finalDue,
        allowpartialpayment: paymentPlan.allowPartialPayment,
        allowmonthlypayment: false,
        allowbookingwithoutpayment:
          tripId !== null || enablePaymentInstructionsFeature
            ? paymentPlan.allowBookingWithoutPayment
            : false,
        tripid: tripId,
        creditcardfeeallocation: paymentPlan.creditCardFeeAllocation,
        achfeeallocation: paymentPlan.achFeeAllocation,
        platformfeeallocation: paymentPlan.platformFeeAllocation
      };
      // Include paymentplanid to update existing record when present
      if (paymentPlan.paymentplanid) {
        paymentPlanData.paymentplanid = Number(paymentPlan.paymentplanid);
      }

      // Initialize all additional payment fields to null
      paymentPlanData.secondpaymentdue = null;
      paymentPlanData.secondpaymentamount = null;
      paymentPlanData.thirdpaymentdue = null;
      paymentPlanData.thirdpaymentamount = null;
      paymentPlanData.fourthpaymentdue = null;
      paymentPlanData.fourthpaymentamount = null;

      // Add only valid additional payments
      const validPayments = paymentPlan.additionalPayments.filter(
        (payment, index) => {
          const amountValid = isValidAdditionalPaymentAmount(
            payment.amountValue,
            payment.amountType
          ).valid;
          const previousPaymentDays =
            index > 0
              ? paymentPlan.additionalPayments[index - 1].daysBeforeDeparture
              : null;
          const daysValid = isValidAdditionalPaymentDays(
            payment.daysBeforeDeparture,
            paymentPlan.finalPaymentDue,
            previousPaymentDays
          ).valid;
          return amountValid && daysValid;
        }
      );

      validPayments.forEach((payment, index) => {
        const paymentNumber = index + 2;
        if (paymentNumber === 2) {
          paymentPlanData.secondpaymentdue = parseInt(
            payment.daysBeforeDeparture
          );
          paymentPlanData.secondpaymentamount = payment.amount; // Keep as string (e.g., "250pp", "30%")
        } else if (paymentNumber === 3) {
          paymentPlanData.thirdpaymentdue = parseInt(
            payment.daysBeforeDeparture
          );
          paymentPlanData.thirdpaymentamount = payment.amount; // Keep as string (e.g., "250pp", "30%")
        } else if (paymentNumber === 4) {
          paymentPlanData.fourthpaymentdue = parseInt(
            payment.daysBeforeDeparture
          );
          paymentPlanData.fourthpaymentamount = payment.amount; // Keep as string (e.g., "250pp", "30%")
        }
      });

      const saveResponse = await createOrUpdatePaymentPlan(
        paymentPlanData,
        tripId
      );
      // Attempt to extract the paymentplanid from response on first create
      const returnedPlan =
        saveResponse?.data?.paymentplan ||
        saveResponse?.paymentplan ||
        saveResponse?.data ||
        saveResponse ||
        null;
      const returnedId =
        returnedPlan?.paymentplanid ||
        returnedPlan?.id ||
        saveResponse?.data?.paymentplanid ||
        saveResponse?.paymentplanid ||
        null;
      if (!paymentPlan.paymentplanid) {
        if (returnedId) {
          setPaymentPlan((prev) => ({ ...prev, paymentplanid: returnedId }));
        } else if (tripId !== null) {
          // As a fallback, fetch the trip-level plan to capture the new id
          try {
            const latest = await getPaymentPlan(tripId);
            const latestPlan =
              latest?.data?.paymentplan || latest?.data || latest || null;
            const latestId =
              latestPlan?.paymentplanid || latestPlan?.id || null;
            if (latestId) {
              setPaymentPlan((prev) => ({ ...prev, paymentplanid: latestId }));
            }
          } catch (_) {
            // ignore; will try again on next edit
          }
        }
      }

      // Also save fee allocations separately
      await saveFeeAllocationsToAPI();

      setFieldErrors({}); // Clear errors on successful save
    } catch (error) {
      console.error(
        t('errors:errorSavingPaymentPlan') || 'Error saving payment plan:',
        error
      );
      ErrorToast(t('errors:failedToSavePaymentPlan'));
    } finally {
      setSaving(false);
    }
  }, [
    t,
    tripId,
    enablePaymentInstructionsFeature,
    paymentPlan,
    validateFormData,
    saveFeeAllocationsToAPI,
    isValidAdditionalPaymentAmount,
    isValidAdditionalPaymentDays
  ]);

  // Auto-save effect - triggers whenever paymentPlan changes
  useEffect(() => {
    if (initialLoadRef.current) return; // Skip initial load
    if (!hasInteracted) return; // Do not auto-save until user interacts
    // For GLOBAL only: skip save while fee allocations still loading
    if (tripId === null) {
      if (
        paymentPlan.creditCardFeeAllocation === null ||
        paymentPlan.achFeeAllocation === null ||
        paymentPlan.platformFeeAllocation === null
      ) {
        return;
      }
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save for 500ms
    saveTimeoutRef.current = setTimeout(() => {
      savePaymentPlanToAPI();
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [paymentPlan, savePaymentPlanToAPI, hasInteracted, tripId]);

  const addPayment = () => {
    if (paymentPlan.additionalPayments.length >= 3) return;

    const paymentIds = ['second', 'third', 'fourth'];
    const nextId = paymentIds[paymentPlan.additionalPayments.length];

    const newPayment = {
      id: nextId,
      amount: '',
      amountType: 'per_person', // default to per person
      amountValue: '',
      daysBeforeDeparture: null
    };

    updatePaymentPlan({
      additionalPayments: [...paymentPlan.additionalPayments, newPayment]
    });
  };

  const removePayment = (paymentId) => {
    // Clear touched state and errors for removed payment
    setTouchedFields((prev) => {
      const updated = { ...prev };
      delete updated[`payment_${paymentId}_amount`];
      delete updated[`payment_${paymentId}_days`];
      return updated;
    });
    setFieldErrors((prev) => {
      const updated = { ...prev };
      delete updated[`payment_${paymentId}_amount`];
      delete updated[`payment_${paymentId}_days`];
      return updated;
    });

    updatePaymentPlan({
      additionalPayments: paymentPlan.additionalPayments.filter(
        (p) => p.id !== paymentId
      )
    });
  };

  const updatePayment = (paymentId, field, value) => {
    // Update the payment first
    const updatedPayments = paymentPlan.additionalPayments.map((p) => {
      if (p.id !== paymentId) return p;

      const updated = { ...p, [field]: value };

      // Auto-update amount string when type or value changes
      if (field === 'amountType' || field === 'amountValue') {
        const type = field === 'amountType' ? value : p.amountType;
        const amount = field === 'amountValue' ? value : p.amountValue;
        updated.amount = formatPaymentAmount(type, amount);
      }

      return updated;
    });

    // Find the current payment and its index for validation
    const currentPaymentIndex = updatedPayments.findIndex(
      (p) => p.id === paymentId
    );

    // Mark field as touched and validate immediately
    if (field === 'amountValue') {
      const fieldKey = `payment_${paymentId}_amount`;
      setTouchedFields((prev) => ({ ...prev, [fieldKey]: true }));

      const currentPayment = updatedPayments.find((p) => p.id === paymentId);
      const validation = isValidAdditionalPaymentAmount(
        currentPayment?.amountValue,
        currentPayment?.amountType
      );
      setFieldErrors((prev) => ({
        ...prev,
        [fieldKey]: validation.valid ? undefined : validation.error
      }));
    } else if (field === 'daysBeforeDeparture') {
      const fieldKey = `payment_${paymentId}_days`;
      setTouchedFields((prev) => ({ ...prev, [fieldKey]: true }));

      const previousPaymentDays =
        currentPaymentIndex > 0
          ? updatedPayments[currentPaymentIndex - 1].daysBeforeDeparture
          : null;
      const validation = isValidAdditionalPaymentDays(
        value,
        paymentPlan.finalPaymentDue,
        previousPaymentDays
      );
      setFieldErrors((prev) => ({
        ...prev,
        [fieldKey]: validation.valid ? undefined : validation.error
      }));
    }

    updatePaymentPlan({
      additionalPayments: updatedPayments
    });
  };

  // Reset trip-specific plan back to global settings by copying global values
  const handleResetToGlobal = async () => {
    if (tripId === null) return;
    try {
      setLoading(true);

      // Load global plan and apply its values locally
      let planResponse = null;
      try {
        planResponse = await getPaymentPlan(null);
      } catch (err) {
        planResponse = null;
      }

      if (planResponse && planResponse.data) {
        const plan = planResponse.data.paymentplan || planResponse.data;

        const additionalPayments = [];
        if (plan.secondpaymentdue && plan.secondpaymentamount) {
          const { type, amount } = parsePaymentAmount(plan.secondpaymentamount);
          additionalPayments.push({
            id: 'second',
            daysBeforeDeparture: plan.secondpaymentdue,
            amount: plan.secondpaymentamount,
            amountType: type,
            amountValue: amount
          });
        }
        if (plan.thirdpaymentdue && plan.thirdpaymentamount) {
          const { type, amount } = parsePaymentAmount(plan.thirdpaymentamount);
          additionalPayments.push({
            id: 'third',
            daysBeforeDeparture: plan.thirdpaymentdue,
            amount: plan.thirdpaymentamount,
            amountType: type,
            amountValue: amount
          });
        }
        if (plan.fourthpaymentdue && plan.fourthpaymentamount) {
          const { type, amount } = parsePaymentAmount(plan.fourthpaymentamount);
          additionalPayments.push({
            id: 'fourth',
            daysBeforeDeparture: plan.fourthpaymentdue,
            amount: plan.fourthpaymentamount,
            amountType: type,
            amountValue: amount
          });
        }

        const { type, amount } = parseDepositAmount(plan.depositamount || '');

        // Apply global plan values locally, preserving existing trip paymentplanid
        setPaymentPlan((prev) => ({
          paymentplanid: prev.paymentplanid || null,
          tenantid: plan.tenantid || null,
          depositAmount: plan.depositamount || '',
          depositType: type,
          depositAmountValue: amount,
          finalPaymentDue: plan.finalpaymentdue || '',
          allowPartialPayment: plan.allowpartialpayment || false,
          allowBookingWithoutPayment: plan.allowbookingwithoutpayment || false,
          additionalPayments,
          // Keep current fee allocations
          creditCardFeeAllocation: prev.creditCardFeeAllocation,
          achFeeAllocation: prev.achFeeAllocation,
          platformFeeAllocation: prev.platformFeeAllocation
        }));

        // Persist these values to the trip's payment plan (update or create)
        const finalDue =
          plan.finalpaymentdue === '' ||
          plan.finalpaymentdue === null ||
          plan.finalpaymentdue === undefined
            ? null
            : parseInt(plan.finalpaymentdue);

        const payload = {
          depositamount: plan.depositamount || '',
          finalpaymentdue: finalDue,
          allowpartialpayment: plan.allowpartialpayment || false,
          allowmonthlypayment: false,
          allowbookingwithoutpayment: plan.allowbookingwithoutpayment || false,
          tripid: tripId,
          secondpaymentdue: plan.secondpaymentdue || null,
          secondpaymentamount: plan.secondpaymentamount || null,
          thirdpaymentdue: plan.thirdpaymentdue || null,
          thirdpaymentamount: plan.thirdpaymentamount || null,
          fourthpaymentdue: plan.fourthpaymentdue || null,
          fourthpaymentamount: plan.fourthpaymentamount || null
        };

        if (paymentPlan?.paymentplanid) {
          payload.paymentplanid = Number(paymentPlan.paymentplanid);
        }

        try {
          const saveResponse = await createOrUpdatePaymentPlan(payload, tripId);
          const returnedPlan =
            saveResponse?.data?.paymentplan ||
            saveResponse?.paymentplan ||
            saveResponse?.data ||
            saveResponse ||
            null;
          const returnedId =
            returnedPlan?.paymentplanid ||
            returnedPlan?.id ||
            saveResponse?.data?.paymentplanid ||
            saveResponse?.paymentplanid ||
            null;
          if (returnedId) {
            setPaymentPlan((prev) => ({ ...prev, paymentplanid: returnedId }));
          }
        } catch (err) {
          console.warn(
            'Failed to persist reset values to trip payment plan.',
            err
          );
        }
      }

      setHasInteracted(false);
    } catch (error) {
      console.error('Error resetting to global settings:', error);
      ErrorToast(t('errors:failedToLoadPaymentPlanData'));
    } finally {
      setLoading(false);
    }
  };

  const openResetConfirm = () => setConfirmResetOpen(true);
  const closeResetConfirm = () => setConfirmResetOpen(false);
  const confirmReset = async () => {
    closeResetConfirm();
    await handleResetToGlobal();
  };

  if (loading) {
    return (
      <Box className='payment-plans-panel__loading'>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box
      className={`payment-plans-container ${tripNavigation ? 'payment-plans-container--fixed' : ''}`}
    >
      {tripId !== null && showPaymentSchedule && (
        <div className='payment-plans-topbar'>
          <button
            type='button'
            className='payment-plans-topbar__reset-btn'
            onClick={openResetConfirm}
            disabled={loading || saving}
          >
            {t('settings:paymentPlan.resetToGlobalSettings') ||
              'Reset to Global Settings'}
          </button>
        </div>
      )}
      <XGModal
        id='reset-payment-plan-modal'
        allowClosing={false}
        data-testid='reset-payment-plan-modal'
        dismissable={false}
        open={confirmResetOpen}
        title={t('settings:paymentPlan.resetConfirm.title')}
        labelledby={t('settings:paymentPlan.resetConfirm.title')}
        maxWidth='lg'
        scroll='body'
        onClose={closeResetConfirm}
        actions={
          <Box
            className='delete-action-modal__footer'
            data-testid='reset-payment-plan-modal-actions'
          >
            <Box sx={{ position: 'relative', width: '50%' }}>
              <XGButton
                style={{ width: '100%' }}
                data-testid='reset-payment-plan-confirm-button'
                id='reset-payment-plan-confirm-button'
                text={t('settings:paymentPlan.resetConfirm.confirm')}
                type='error'
                size='large'
                variant='outlined'
                onClick={confirmReset}
                disabled={loading || saving}
              />
              {(loading || saving) && (
                <CircularProgress
                  id='reset-payment-plan-loader'
                  size={24}
                  sx={{
                    color: blue[500],
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px'
                  }}
                />
              )}
            </Box>
            <XGButton
              data-testid='reset-payment-plan-cancel-button'
              id='reset-payment-plan-cancel-button'
              text={t('settings:paymentPlan.resetConfirm.cancel')}
              type='primary'
              size='large'
              variant='contained'
              onClick={closeResetConfirm}
              disabled={loading || saving}
            />
          </Box>
        }
      >
        <Box className='create-trip-header' sx={{ maxWidth: '400px' }}>
          <p className='text-md-regular'>
            {`${t('settings:paymentPlan.resetConfirm.message')} ${t('common:thisActionCannotBeUndone')}`}
          </p>
        </Box>
      </XGModal>
      <div className='payment-content-with-sidebar'>
        <div className='payment-main-content'>
          {/* Initial Deposit Card */}
          {showPaymentSchedule && (
            <div className='initial-deposit-card'>
              <div className='initial-deposit-header'>
                <h3 className='initial-deposit-title'>
                  {t('settings:paymentPlan.initialDeposit')}
                </h3>
              </div>

              <div className='initial-deposit-content'>
                {/* Calculation Method - Full Width Row */}
                <div className='calculation-method-section'>
                  <label className='form-label'>
                    {t('settings:paymentPlan.calculationMethod')}
                  </label>
                  <SegmentedToggle
                    selectedValue={paymentPlan.depositType}
                    onChange={(value) =>
                      updatePaymentPlan({ depositType: value })
                    }
                    options={[
                      {
                        label: t('settings:paymentPlan.perPerson'),
                        value: 'per_person'
                      },
                      {
                        label: t('settings:paymentPlan.perBooking'),
                        value: 'per_booking',
                        matchValues: ['per_booking', 'percentage']
                      }
                    ]}
                  />
                </div>

                {/* Deposit Type and Amount - Same Row */}
                <div className='deposit-type-amount-row'>
                  <div className='deposit-type-section'>
                    <label className='form-label'>
                      {t('settings:paymentPlan.depositType')}
                    </label>
                    {paymentPlan.depositType === 'per_person' ? (
                      <div className='deposit-type-display'>
                        {t('settings:paymentPlan.fixedAmountPerPerson')}
                      </div>
                    ) : (
                      <select
                        className='deposit-type-dropdown'
                        value={
                          paymentPlan.depositType === 'percentage'
                            ? 'percentage'
                            : 'fixed'
                        }
                        onChange={(e) =>
                          updatePaymentPlan({
                            depositType:
                              e.target.value === 'percentage'
                                ? 'percentage'
                                : 'per_booking'
                          })
                        }
                      >
                        <option value='fixed'>
                          {t('settings:paymentPlan.fixedAmountPerBooking')}
                        </option>
                        <option value='percentage'>
                          {t('settings:paymentPlan.percentageOfTotal')}
                        </option>
                      </select>
                    )}
                  </div>

                  <div className='amount-section'>
                    <label className='form-label'>
                      {t('settings:paymentPlan.amount')}
                    </label>
                    <div className='amount-input-wrapper'>
                      {paymentPlan.depositType !== 'percentage' && (
                        <span className='amount-input-prefix'>$</span>
                      )}
                      <input
                        type='number'
                        className={`amount-input ${paymentPlan.depositType === 'percentage' ? 'amount-input-no-prefix' : ''}`}
                        placeholder={
                          paymentPlan.depositType === 'percentage'
                            ? '25'
                            : '100'
                        }
                        value={paymentPlan.depositAmountValue}
                        onChange={(e) =>
                          updatePaymentPlan({
                            depositAmountValue: e.target.value
                          })
                        }
                      />
                      {paymentPlan.depositType === 'percentage' && (
                        <span className='amount-input-suffix'>%</span>
                      )}
                    </div>
                    {fieldErrors.depositAmountValue && (
                      <div className='field-error'>
                        {fieldErrors.depositAmountValue}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Final Payment Card */}
          {showPaymentSchedule && (
            <div className='final-payment-card'>
              <div className='final-payment-header'>
                <h3 className='final-payment-title'>
                  {t('settings:paymentPlan.finalPayment')}
                </h3>
              </div>

              <div className='final-payment-content'>
                <div className='final-payment-due-row'>
                  <div className='due-section'>
                    <label className='final-payment-label'>
                      {t('settings:paymentPlan.dueDate')}
                    </label>
                    <div className='due-input-wrapper'>
                      {tripId !== null && tripStartDate ? (
                        <XGDatePicker
                          format={'dd MMM yyyy'}
                          id={'final-payment-due-date'}
                          name={'finalPaymentDueDate'}
                          value={
                            paymentPlan.finalPaymentDue
                              ? subDays(
                                  new Date(tripStartDate),
                                  Number(paymentPlan.finalPaymentDue || 0)
                                )
                              : null
                          }
                          onChange={(selectedDate) => {
                            if (!selectedDate) {
                              updatePaymentPlan({ finalPaymentDue: '' });
                              return;
                            }
                            const diff = differenceInCalendarDays(
                              new Date(tripStartDate),
                              selectedDate
                            );
                            updatePaymentPlan({
                              finalPaymentDue: isNaN(diff) ? '' : String(diff)
                            });
                          }}
                          slotProps={{ field: { clearable: true } }}
                        />
                      ) : (
                        <>
                          <input
                            type='number'
                            className='due-input'
                            placeholder='7'
                            value={paymentPlan.finalPaymentDue}
                            onChange={(e) =>
                              updatePaymentPlan({
                                finalPaymentDue: e.target.value
                              })
                            }
                          />
                          <span className='due-input-suffix'>
                            {t('settings:paymentPlan.daysBeforeTripStart')}
                          </span>
                        </>
                      )}
                    </div>
                    {tripId !== null &&
                      tripStartDate &&
                      paymentPlan.finalPaymentDue !== '' && (
                        <div className='help-text'>
                          {t(
                            'settings:paymentPlan.daysBeforeTripStartWithCount',
                            {
                              days: paymentPlan.finalPaymentDue
                            }
                          )}
                        </div>
                      )}
                    {fieldErrors.finalPaymentDue && (
                      <div className='field-error'>
                        {fieldErrors.finalPaymentDue}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Payments Card */}
          {showPaymentSchedule && (
            <div className='additional-payments-card'>
              <div className='additional-payments-header'>
                <h3 className='additional-payments-title'>
                  {t('settings:paymentPlan.additionalPayments')}
                </h3>
              </div>

              <div className='additional-payments-content'>
                {paymentPlan.additionalPayments.length === 0 ? (
                  <div className='additional-payments-empty'>
                    <div className='additional-payments-empty-title'>
                      {t('settings:paymentPlan.noAdditionalPayments')}
                    </div>
                    <div className='additional-payments-empty-subtitle'>
                      {t('settings:paymentPlan.additionalPaymentsHelp')}
                    </div>
                    <button
                      className='add-first-payment-btn'
                      onClick={addPayment}
                    >
                      {t('settings:paymentPlan.addFirstPayment')}
                    </button>
                  </div>
                ) : (
                  <div className='additional-payments-list'>
                    {paymentPlan.additionalPayments.map((payment, index) => (
                      <div key={payment.id} className='additional-payment-item'>
                        <div className='additional-payment-header'>
                          <div className='additional-payment-title'>
                            {t('settings:paymentPlan.paymentNumber', {
                              number: index + 2
                            })}
                          </div>
                          {index ===
                            paymentPlan.additionalPayments.length - 1 && (
                            <button
                              className='additional-payment-delete'
                              onClick={() => removePayment(payment.id)}
                              title={t('settings:paymentPlan.deletePayment')}
                            >
                              <Trash size={16} />
                            </button>
                          )}
                        </div>

                        <div className='additional-payment-fields'>
                          {/* Calculation Method */}
                          <div className='additional-payment-field full-width'>
                            <label className='additional-payment-label'>
                              {t('settings:paymentPlan.calculationMethod')}
                            </label>
                            <SegmentedToggle
                              selectedValue={payment.amountType}
                              onChange={(value) =>
                                updatePayment(payment.id, 'amountType', value)
                              }
                              options={[
                                {
                                  label: t('settings:paymentPlan.perPerson'),
                                  value: 'per_person'
                                },
                                {
                                  label: t('settings:paymentPlan.perBooking'),
                                  value: 'per_booking',
                                  matchValues: ['per_booking', 'percentage']
                                }
                              ]}
                            />
                          </div>

                          {/* Payment Type, Amount, and Due Date Row */}
                          <div className='additional-payment-type-amount-row'>
                            <div className='additional-payment-type-section'>
                              <label className='additional-payment-label'>
                                {t('settings:paymentPlan.paymentType')}
                              </label>
                              {payment.amountType === 'per_person' ? (
                                <div className='additional-payment-type-display'>
                                  {t(
                                    'settings:paymentPlan.fixedAmountPerPerson'
                                  )}
                                </div>
                              ) : (
                                <select
                                  className='additional-payment-type-dropdown'
                                  value={
                                    payment.amountType === 'percentage'
                                      ? 'percentage'
                                      : 'fixed'
                                  }
                                  onChange={(e) =>
                                    updatePayment(
                                      payment.id,
                                      'amountType',
                                      e.target.value === 'percentage'
                                        ? 'percentage'
                                        : 'per_booking'
                                    )
                                  }
                                >
                                  <option value='fixed'>
                                    {t(
                                      'settings:paymentPlan.fixedAmountPerBooking'
                                    )}
                                  </option>
                                  <option value='percentage'>
                                    {t(
                                      'settings:paymentPlan.percentageOfTotal'
                                    )}
                                  </option>
                                </select>
                              )}
                            </div>

                            <div className='additional-payment-amount-section'>
                              <label className='additional-payment-label'>
                                {t('settings:paymentPlan.amount')}
                              </label>
                              <div className='additional-payment-input-wrapper'>
                                {payment.amountType !== 'percentage' && (
                                  <span className='additional-payment-prefix'>
                                    $
                                  </span>
                                )}
                                <input
                                  type='number'
                                  className={`additional-payment-input ${payment.amountType === 'percentage' ? 'additional-payment-input-no-prefix' : ''}`}
                                  placeholder={t(
                                    'settings:paymentPlan.amountPlaceholder'
                                  )}
                                  value={payment.amountValue || ''}
                                  onChange={(e) =>
                                    updatePayment(
                                      payment.id,
                                      'amountValue',
                                      e.target.value
                                    )
                                  }
                                />
                                {payment.amountType === 'percentage' && (
                                  <span className='additional-payment-suffix'>
                                    %
                                  </span>
                                )}
                              </div>
                              {fieldErrors[`payment_${payment.id}_amount`] && (
                                <div className='field-error'>
                                  {fieldErrors[`payment_${payment.id}_amount`]}
                                </div>
                              )}
                            </div>

                            <div className='additional-payment-due-section'>
                              <label className='additional-payment-label'>
                                {t('settings:paymentPlan.dueDate')}
                              </label>
                              <div className='additional-payment-due-wrapper'>
                                {tripId !== null && tripStartDate ? (
                                  <XGDatePicker
                                    format={'dd MMM yyyy'}
                                    id={`additional-payment-due-date-${payment.id}`}
                                    name={`additionalPaymentDueDate_${payment.id}`}
                                    value={
                                      payment.daysBeforeDeparture
                                        ? subDays(
                                            toLocalNoon(tripStartDate),
                                            Number(
                                              payment.daysBeforeDeparture || 0
                                            )
                                          )
                                        : null
                                    }
                                    onChange={(selectedDate) => {
                                      if (!selectedDate) {
                                        updatePayment(
                                          payment.id,
                                          'daysBeforeDeparture',
                                          ''
                                        );
                                        return;
                                      }
                                      const startNoon =
                                        toLocalNoon(tripStartDate);
                                      const selNoon = toLocalNoon(selectedDate);
                                      const diff = differenceInCalendarDays(
                                        startNoon,
                                        selNoon
                                      );
                                      updatePayment(
                                        payment.id,
                                        'daysBeforeDeparture',
                                        isNaN(diff) ? '' : String(diff)
                                      );
                                    }}
                                    slotProps={{ field: { clearable: true } }}
                                  />
                                ) : (
                                  <>
                                    <input
                                      type='number'
                                      className='additional-payment-input'
                                      placeholder={t(
                                        'settings:paymentPlan.daysPlaceholder'
                                      )}
                                      value={payment.daysBeforeDeparture}
                                      onChange={(e) =>
                                        updatePayment(
                                          payment.id,
                                          'daysBeforeDeparture',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <span className='additional-payment-suffix'>
                                      {t(
                                        'settings:paymentPlan.daysBeforeTripStart'
                                      )}
                                    </span>
                                  </>
                                )}
                              </div>
                              {tripId !== null &&
                                tripStartDate &&
                                payment.daysBeforeDeparture !== '' && (
                                  <div className='help-text'>
                                    {t(
                                      'settings:paymentPlan.daysBeforeTripStartWithCount',
                                      {
                                        days: payment.daysBeforeDeparture
                                      }
                                    )}
                                  </div>
                                )}
                              {fieldErrors[`payment_${payment.id}_days`] && (
                                <div className='field-error'>
                                  {fieldErrors[`payment_${payment.id}_days`]}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {paymentPlan.additionalPayments.length < 3 && (
                      <div className='add-another-payment'>
                        <button
                          className='add-another-payment-btn'
                          onClick={addPayment}
                        >
                          <Plus size={16} />
                          {t('settings:paymentPlan.addAnotherPayment')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enable and Fee Allocation Cards Row */}
          {showTogglesAndFees && (
            <div
              className={`enable-fee-allocation-row ${!showPaymentSchedule ? 'enable-fee-allocation-row--stacked' : ''}`}
            >
              {/* Enable Card */}
              <div className='enable-card'>
                <div className='enable-header'>
                  <h3 className='enable-title'>
                    {t('settings:paymentPlan.enable')}
                  </h3>
                </div>

                <div className='enable-content'>
                  <div className='enable-option'>
                    <div className='enable-option-header'>
                      <div className='enable-option-info'>
                        <div className='enable-option-title'>
                          {t('settings:paymentPlan.partialPayments')}
                        </div>
                      </div>
                      <div
                        className={`toggle-switch ${paymentPlan.allowPartialPayment ? 'active' : ''}`}
                        onClick={() =>
                          updatePaymentPlan({
                            allowPartialPayment:
                              !paymentPlan.allowPartialPayment
                          })
                        }
                      ></div>
                    </div>
                    <div className='enable-option-description'>
                      {t('settings:paymentPlan.partialPaymentsDescription')}
                    </div>
                  </div>

                  {(tripId !== null ||
                    (tripId === null && enablePaymentInstructionsFeature)) && (
                    <div className='enable-option'>
                      <div className='enable-option-header'>
                        <div className='enable-option-info'>
                          <div className='enable-option-title'>
                            {t('settings:paymentPlan.completeWithoutPayment')}
                          </div>
                        </div>
                        <div
                          className={`toggle-switch ${paymentPlan.allowBookingWithoutPayment ? 'active' : ''}`}
                          onClick={() =>
                            updatePaymentPlan({
                              allowBookingWithoutPayment:
                                !paymentPlan.allowBookingWithoutPayment
                            })
                          }
                        ></div>
                      </div>
                      <div className='enable-option-description'>
                        {t(
                          'settings:paymentPlan.completeWithoutPaymentDescription'
                        )}
                      </div>
                    </div>
                  )}

                  {enablePaymentInstructionsFeature &&
                    paymentPlan.allowBookingWithoutPayment && (
                      <div className='payment-instructions-editor'>
                        <div className='payment-instructions-editor__header'>
                          <div className='enable-option-title'>
                            {t('settings:paymentPlan.paymentInstructions')}
                          </div>
                          <div className='payment-instructions-editor__counter'>
                            {paymentInstructionsLength}/
                            {PAYMENT_INSTRUCTIONS_MAX_LENGTH}
                          </div>
                        </div>
                        <div className='enable-option-description'>
                          {t(
                            'settings:paymentPlan.paymentInstructionsDescription'
                          )}
                        </div>
                        <Editor
                          id='global-payment-instructions-editor'
                          editable={true}
                          isPreviewing={false}
                          content={savedPaymentInstructions}
                          fontSize={{ xs: '14px', md: '14px' }}
                          menuItems={{
                            heading: false,
                            bold: true,
                            italic: true,
                            bulletList: true,
                            orderedList: true,
                            link: true,
                            undoRedo: false
                          }}
                          setDescription={() => {}}
                          setMarkdown={setPaymentInstructions}
                          showActions={false}
                          maxLength={PAYMENT_INSTRUCTIONS_MAX_LENGTH}
                          onLengthChange={setPaymentInstructionsLength}
                          onBlurAction={savePaymentInstructionsOnBlur}
                          placeholder={t(
                            'settings:paymentPlan.paymentInstructionsPlaceholder'
                          )}
                          className='payment-instructions-editor__control'
                        />
                      </div>
                    )}
                </div>
              </div>

              {/* Fee Allocation Card */}
              <div className='fee-allocation-card'>
                <div className='fee-allocation-header'>
                  <h3 className='fee-allocation-title'>
                    {t('settings:paymentPlan.feeAllocation')}
                  </h3>
                </div>

                <div className='fee-allocation-content'>
                  <div className='fee-allocation-option'>
                    <div className='fee-allocation-option-title'>
                      {t('settings:paymentPlan.creditCardFees')}
                    </div>
                    <Box sx={{ display: 'flex', gap: 0 }}>
                      <XGRadio
                        id='credit-card-fee-organizer'
                        name='creditCardFeeAllocation'
                        value='organizer'
                        variant='pill'
                        size='small'
                        checked={
                          paymentPlan.creditCardFeeAllocation === 'organizer'
                        }
                        onChange={() =>
                          handleFeeAllocationChange(
                            'creditCardFeeAllocation',
                            'organizer'
                          )
                        }
                        label={t('settings:paymentPlan.organizerPays')}
                        disabled={paymentPlan.creditCardFeeAllocation === null}
                      />
                      <XGRadio
                        id='credit-card-fee-passenger'
                        name='creditCardFeeAllocation'
                        value='participant'
                        variant='pill'
                        size='small'
                        checked={
                          paymentPlan.creditCardFeeAllocation === 'participant'
                        }
                        onChange={() =>
                          handleFeeAllocationChange(
                            'creditCardFeeAllocation',
                            'participant'
                          )
                        }
                        label={t('settings:paymentPlan.passengerPays')}
                        disabled={paymentPlan.creditCardFeeAllocation === null}
                      />
                    </Box>
                  </div>

                  <div className='fee-allocation-option'>
                    <div className='fee-allocation-option-title'>
                      {t('settings:paymentPlan.achFees')}
                    </div>
                    <Box sx={{ display: 'flex', gap: 0 }}>
                      <XGRadio
                        id='ach-fee-organizer'
                        name='achFeeAllocation'
                        value='organizer'
                        variant='pill'
                        size='small'
                        checked={paymentPlan.achFeeAllocation === 'organizer'}
                        onChange={() =>
                          handleFeeAllocationChange(
                            'achFeeAllocation',
                            'organizer'
                          )
                        }
                        label={t('settings:paymentPlan.organizerPays')}
                        disabled={paymentPlan.achFeeAllocation === null}
                      />
                      <XGRadio
                        id='ach-fee-passenger'
                        name='achFeeAllocation'
                        value='participant'
                        variant='pill'
                        size='small'
                        checked={paymentPlan.achFeeAllocation === 'participant'}
                        onChange={() =>
                          handleFeeAllocationChange(
                            'achFeeAllocation',
                            'participant'
                          )
                        }
                        label={t('settings:paymentPlan.passengerPays')}
                        disabled={paymentPlan.achFeeAllocation === null}
                      />
                    </Box>
                  </div>

                  <div className='fee-allocation-option'>
                    <div className='fee-allocation-option-title'>
                      {t('settings:paymentPlan.platformFee')}
                    </div>
                    <Box sx={{ display: 'flex', gap: 0 }}>
                      <XGRadio
                        id='platform-fee-organizer'
                        name='platformFeeAllocation'
                        value='organizer'
                        variant='pill'
                        size='small'
                        checked={
                          paymentPlan.platformFeeAllocation === 'organizer'
                        }
                        onChange={() =>
                          handleFeeAllocationChange(
                            'platformFeeAllocation',
                            'organizer'
                          )
                        }
                        label={t('settings:paymentPlan.organizerPays')}
                        disabled={paymentPlan.platformFeeAllocation === null}
                      />
                      <XGRadio
                        id='platform-fee-passenger'
                        name='platformFeeAllocation'
                        value='participant'
                        variant='pill'
                        size='small'
                        checked={
                          paymentPlan.platformFeeAllocation === 'participant'
                        }
                        onChange={() =>
                          handleFeeAllocationChange(
                            'platformFeeAllocation',
                            'participant'
                          )
                        }
                        label={t('settings:paymentPlan.passengerPays')}
                        disabled={paymentPlan.platformFeeAllocation === null}
                      />
                    </Box>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Schedule Summary/Sidebat */}
        {showPaymentSchedule && (
          <div className='payment-schedule-sidebar'>
            <div className='payment-schedule-card'>
              <div className='payment-schedule-header'>
                {/* Title */}
                <h3 className='payment-schedule-title'>
                  {t('settings:paymentPlan.paymentSchedule')}
                </h3>
              </div>

              {/* Content */}
              <div className='payment-schedule-content'>
                {!paymentPlan.finalPaymentDue &&
                !paymentPlan.depositAmountValue &&
                paymentPlan.additionalPayments.length === 0 ? (
                  <div className='payment-schedule-empty'>
                    <p>{t('settings:paymentPlan.configureToSeeSchedule')}</p>
                  </div>
                ) : (
                  <div className='payment-schedule-items'>
                    {paymentPlan.depositAmountValue && (
                      <div className='payment-schedule-item'>
                        <div className='payment-schedule-item-info'>
                          <div className='payment-schedule-item-title'>
                            {t('settings:paymentPlan.initialDeposit')}
                          </div>
                          <div className='payment-schedule-item-subtitle'>
                            {paymentPlan.depositType === 'per_person'
                              ? t('settings:paymentPlan.perPersonLower')
                              : t('settings:paymentPlan.perBookingLower')}
                          </div>
                        </div>
                        <div className='payment-schedule-item-amount'>
                          {paymentPlan.depositType === 'percentage'
                            ? `${paymentPlan.depositAmountValue}%`
                            : `$${paymentPlan.depositAmountValue}`}
                        </div>
                      </div>
                    )}

                    {paymentPlan.additionalPayments.map(
                      (payment, index) =>
                        payment.amount &&
                        payment.daysBeforeDeparture && (
                          <div
                            key={payment.id}
                            className='payment-schedule-item'
                          >
                            <div className='payment-schedule-item-info'>
                              <div className='payment-schedule-item-title'>
                                {t('settings:paymentPlan.paymentNumber', {
                                  number: index + 2
                                })}
                              </div>
                              <div className='payment-schedule-item-subtitle'>
                                {tripId !== null && tripStartDate
                                  ? format(
                                      subDays(
                                        toLocalNoon(tripStartDate),
                                        Number(payment.daysBeforeDeparture || 0)
                                      ),
                                      'dd MMM yyyy'
                                    )
                                  : t(
                                      'settings:paymentPlan.daysBeforeTripStartWithCount',
                                      {
                                        days: payment.daysBeforeDeparture
                                      }
                                    )}
                              </div>
                            </div>
                            <div className='payment-schedule-item-amount'>
                              <div>
                                {payment.amountType === 'percentage'
                                  ? `${payment.amountValue}%`
                                  : `$${payment.amountValue}`}
                              </div>
                              {payment.amountType !== 'percentage' && (
                                <div className='payment-schedule-item-type'>
                                  {payment.amountType === 'per_person'
                                    ? t('settings:paymentPlan.perPersonLower')
                                    : t('settings:paymentPlan.perBookingLower')}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                    )}

                    {paymentPlan.finalPaymentDue && (
                      <div className='payment-schedule-item'>
                        <div className='payment-schedule-item-info'>
                          <div className='payment-schedule-item-title'>
                            {t('settings:paymentPlan.finalPayment')}
                          </div>
                          <div className='payment-schedule-item-subtitle'>
                            {tripId !== null && tripStartDate
                              ? format(
                                  subDays(
                                    toLocalNoon(tripStartDate),
                                    Number(paymentPlan.finalPaymentDue || 0)
                                  ),
                                  'dd MMM yyyy'
                                )
                              : t(
                                  'settings:paymentPlan.daysBeforeTripStartWithCount',
                                  { days: paymentPlan.finalPaymentDue }
                                )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className='payment-schedule-note'>
                  {paymentPlan.allowPartialPayment && (
                    <div>
                      {t('settings:paymentPlan.partialPaymentsEnabled')}
                    </div>
                  )}
                  {paymentPlan.allowBookingWithoutPayment && (
                    <div>
                      {t('settings:paymentPlan.bookingWithoutPaymentEnabled')}
                    </div>
                  )}
                  {!showTogglesAndFees &&
                    (paymentPlan.allowPartialPayment ||
                      paymentPlan.allowBookingWithoutPayment) && (
                      <div style={{ fontSize: '10px' }}>
                        {t('settings:paymentPlan.toggleInstructions')}
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

PaymentPlansPanel.propTypes = {
  id: PropTypes.string,
  tripId: PropTypes.number,
  showPaymentSchedule: PropTypes.bool,
  showTogglesAndFees: PropTypes.bool
};

export default PaymentPlansPanel;
